/**
 * Inline-style indent format for the Quill template editors.
 *
 * Quill's default indent format writes a `ql-indent-N` class. Email clients drop
 * stylesheet classes, so this attributor writes `margin-left` in pixels instead
 * (https://github.com/quilljs/quill/issues/2351).
 *
 * 1. Quill's Tab and Shift+Tab bindings call `add(node, '+1')` or `add(node, '-1')`.
 * 2. `add` reads the current level from the node, applies the step, and writes
 *    `level * TAB_MULTIPLIER` pixels. Level 0 removes the style.
 * 3. A delta, paste, or `setContents` calls `add(node, N)` with the numeric level.
 * 4. `value` converts the pixel width back to the level that deltas carry.
 *
 * The level lives only on the DOM node. The attributor is a shared singleton, so
 * it must not keep per-line state. Continues in `registerQuillModules.ts`.
 */
import { err, ok, Result } from 'neverthrow';
import { Quill } from 'react-quill';

const MAX_INDENT_LEVEL = 8;
const PIXEL_LEVELS = [1, 2, 3, 4, 5, 6, 7, 8];
/** Pixels of `margin-left` per indent level. `nestQuillListHtml.ts` reads levels back with it. */
export const TAB_MULTIPLIER = 30;

const Parchment = Quill.import('parchment');

/** One change Quill asks the format to make to a line's indent. */
type IndentChange = { kind: 'STEP'; direction: 1 | -1 } | { kind: 'LEVEL'; level: number };

/** The expected failure of `parseIndentChange`: Quill passed a value that is not an indent (ADR 0022). */
type ParseIndentChangeError = { type: 'NOT_AN_INDENT_VALUE'; raw: string };

/**
 * Parses the raw value Quill passes to `add` at the I/O boundary.
 * `'+1'` and `'-1'` come from the Tab bindings and toolbar buttons. A numeric
 * level comes from a delta, paste, or `setContents`. Any other value is an
 * expected failure, which `add` maps to Parchment's "nothing applied" result.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parseIndentChange = (value: any): Result<IndentChange, ParseIndentChangeError> => {
  if (value === '+1') {
    return ok({ kind: 'STEP', direction: 1 });
  }
  if (value === '-1') {
    return ok({ kind: 'STEP', direction: -1 });
  }
  const level = Number(value);
  if (Number.isNaN(level)) {
    return err({ type: 'NOT_AN_INDENT_VALUE', raw: String(value) });
  }
  return ok({ kind: 'LEVEL', level });
};

/** Keeps a level inside the whitelisted range `0..MAX_INDENT_LEVEL`. */
const clampIndentLevel = (level: number): number => Math.min(Math.max(Math.trunc(level), 0), MAX_INDENT_LEVEL);

/** Applies one change to the level currently stored on the line. */
const nextIndentLevel = (current: number, change: IndentChange): number =>
  change.kind === 'STEP' ? clampIndentLevel(current + change.direction) : clampIndentLevel(change.level);

/**
 * Style attributor that stores the indent level as a `margin-left` width.
 * Mirrors Quill's own `IndentClass` contract: `add` returns `true` when the
 * node changed, and level 0 removes the style instead of writing `0px`.
 */
class IndentAttributor extends Parchment.Attributor.Style {
  // `Quill.import` returns `any`, so without this constructor TypeScript types the
  // subclass as taking zero constructor arguments.
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(
    formatName: string,
    styleProperty: string,
    attributorOptions: { scope: number; whitelist: readonly string[] }
  ) {
    super(formatName, styleProperty, attributorOptions);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  add(node: any, value: any): boolean {
    return parseIndentChange(value).match(
      (change) => {
        const level = nextIndentLevel(this.value(node) ?? 0, change);
        if (level === 0) {
          super.remove(node);
          return true;
        }
        return super.add(node, `${level * TAB_MULTIPLIER}px`);
      },
      // Parchment's contract: `add` returns false when it applied nothing.
      () => false
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value(node: any): number | undefined {
    return parseFloat(super.value(node)) / TAB_MULTIPLIER || undefined; // Don't return NaN
  }
}

const IndentStyle = new IndentAttributor('indent', 'margin-left', {
  scope: Parchment.Scope.BLOCK,
  whitelist: PIXEL_LEVELS.map((value) => `${value * TAB_MULTIPLIER}px`),
});

export default IndentStyle;
